import { useState } from "react";
import { api, type ConnectedAccount, type PlatformInfo } from "../api";
import { PlatformIcon } from "./PlatformIcon";

// A persistent, at-a-glance strip of every integration's connection state.
// Lives at the top of the dashboard because the calendar moved Accounts behind
// a Settings panel — without this, you couldn't see what's connected without a
// click. This shows CONNECTION only (connected / connectable / needs keys);
// the separate "will it publish" nuance (e.g. TikTok's audit) lives on the
// calendar, so the two concerns don't get muddled here.

type ConnState = "connected" | "reconnect" | "connect" | "unconfigured";

function stateFor(p: PlatformInfo, account: ConnectedAccount | undefined): ConnState {
  // A connected-but-stale account (revoked/expired token) gets its own amber
  // state so it isn't mistaken for healthy (#5).
  if (account) return account.needsReconnect ? "reconnect" : "connected";
  return p.configured ? "connect" : "unconfigured";
}

const LABEL: Record<ConnState, string> = {
  connected: "Connected",
  reconnect: "Reconnect",
  connect: "Connect",
  unconfigured: "Coming soon",
};

/**
 * Public URL of a connected account's own page on the platform, or null when we
 * don't hold a reliable identifier to build one. Only the platforms whose stored
 * id/handle maps cleanly to a public URL are linked:
 *   • YouTube  → channel id (UC…)     • Instagram → username (displayName)
 *   • Facebook → page id               • TikTok    → captured @username
 * TikTok links only once its @username is captured (at connect, or on the first
 * compose that loads creator_info); until then it has only an opaque open_id, so
 * no link. X (handle not stored) is intentionally not linked, so we never
 * produce a dead link.
 */
export function accountUrl(platform: string, account: ConnectedAccount): string | null {
  const id = account.accountId?.trim();
  const name = account.displayName?.trim();
  const handle = account.username?.trim();
  switch (platform) {
    case "youtube":
      return id ? `https://www.youtube.com/channel/${encodeURIComponent(id)}` : null;
    case "instagram":
      return name ? `https://www.instagram.com/${encodeURIComponent(name.replace(/^@/, ""))}/` : null;
    case "facebook":
      return id ? `https://www.facebook.com/${encodeURIComponent(id)}` : null;
    case "tiktok":
      return handle ? `https://www.tiktok.com/@${encodeURIComponent(handle.replace(/^@/, ""))}` : null;
    default:
      return null;
  }
}

export function ConnectionBar({
  platforms,
  accounts,
  onManage,
}: {
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  /** Open the Settings panel — used for connected/unconfigured chips. */
  onManage: () => void;
}) {
  const byPlatform = new Map(accounts.map((a) => [a.platform, a]));
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <div className="conn-bar" aria-label="Integration connections">
        {platforms.map((p) => {
          const account = byPlatform.get(p.id);
          const state = stateFor(p, account);
          // A connectable OR reconnect-needed platform jumps straight into OAuth;
          // everything else opens Settings, where connect/disconnect and billing
          // live. A failure to even get the consent URL is now surfaced (#2).
          const onClick = async () => {
            if (state === "connect" || state === "reconnect") {
              setError(null);
              try {
                const { url } = await api.startConnect(p.id);
                window.location.href = url;
              } catch (err) {
                setError(
                  err instanceof Error
                    ? `${p.name}: ${err.message}`
                    : `Couldn't start connecting ${p.name}.`,
                );
              }
              return;
            }
            onManage();
          };
          // A connected account whose public page we can address becomes a link
          // straight to that account (new tab); everything else stays a button
          // (connect/reconnect → OAuth, otherwise → Settings).
          const url = state === "connected" && account ? accountUrl(p.id, account) : null;
          const inner = (
            <>
              <PlatformIcon platform={p} size={20} />
              <span className="conn-chip-name">{p.name}</span>
              <span className={`conn-chip-dot conn-chip-dot--${state}`} aria-hidden="true" />
              <span className="conn-chip-state">
                {state === "connected" ? account!.displayName : LABEL[state]}
                {url && <span className="conn-chip-ext" aria-hidden="true"> ↗</span>}
              </span>
            </>
          );
          if (url) {
            return (
              <a
                key={p.id}
                className="conn-chip conn-chip--connected"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${account!.displayName} on ${p.name} ↗`}
              >
                {inner}
              </a>
            );
          }
          return (
            <button
              key={p.id}
              type="button"
              className={`conn-chip conn-chip--${state}`}
              onClick={onClick}
              title={
                state === "reconnect"
                  ? `${p.name}: reconnect needed`
                  : account
                    ? `${p.name}: connected as ${account.displayName}`
                    : `${p.name}: ${LABEL[state]}`
              }
            >
              {inner}
            </button>
          );
        })}
      </div>
      {error && <p className="error">{error}</p>}
    </>
  );
}
